import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { QueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { FlareFloatingWindow } from "flare-ui";
import { Icon } from "@/components/ui-components/icon/icon";
import { assetUrl } from "@/utils/assets";

interface ErrorDialogContextValue {
    showError: (error: unknown, title?: string, detailsOverride?: string) => void;
    dismiss: () => void;
}

const ErrorDialogContext = createContext<ErrorDialogContextValue | null>(null);

function resolveErrorMessage(error: unknown): { title: string; message: string; details: string | null } {
    if (error instanceof Error && error.message) {
        return {
            title: "Something went wrong",
            message: error.message,
            details: error.toString() + (error.stack ? `\n${error.stack}` : "")
        };
    }

    if (typeof error === "string" && error.trim()) {
        return {
            title: "Something went wrong",
            message: error,
            details: error
        };
    }

    if (error && typeof error === "object") {
        const message = "message" in error && typeof (error as { message?: unknown }).message === "string"
            ? (error as { message: string }).message
            : undefined;

        if (message) {
            const fallback = typeof (error as { toString?: () => string }).toString === "function"
                ? (error as { toString: () => string }).toString()
                : JSON.stringify(error, null, 2);

            return {
                title: "Something went wrong",
                message,
                details: fallback !== "[object Object]" ? fallback : JSON.stringify(error, null, 2)
            };
        }

        if (typeof (error as { toString?: () => string }).toString === "function") {
            const asString = (error as { toString: () => string }).toString();
            if (asString && asString !== "[object Object]") {
                return {
                    title: "Something went wrong",
                    message: asString,
                    details: asString
                };
            }
        }
    }

    return {
        title: "Something went wrong",
        message: "Something went wrong. Please try again.",
        details: typeof error === "object" && error ? JSON.stringify(error, null, 2) : String(error ?? "Something went wrong. Please try again.")
    };
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function GlobalErrorDialogProvider({ children, queryClient }: { children: React.ReactNode; queryClient?: QueryClient }) {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);
    const [title, setTitle] = useState(t("pages.errorDialog.title"));
    const [message, setMessage] = useState(t("pages.errorDialog.defaultMessage"));
    const [details, setDetails] = useState<string | null>(null);
    const [ticket, setTicket] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const [showDetails, setShowDetails] = useState(false);
    const [dialogRect, setDialogRect] = useState({ left: 140, top: 120, width: 620, height: 300 });
    const lastErrorKey = useRef<string | null>(null);
    const isRtl = i18n.dir() === "rtl";
    const isCompact = typeof window !== "undefined" && window.innerWidth < 480;

    const updateDialogRect = useCallback(() => {
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const maxWidth = Math.max(280, Math.min(viewportWidth - 24, 760));
        const maxHeight = Math.max(180, Math.min(viewportHeight - 52, 420));
        const width = clamp(Math.min(viewportWidth - 24, 620), 280, maxWidth);
        const height = clamp(Math.min(viewportHeight - 60, 300), 180, maxHeight);
        const left = Math.max(12, (viewportWidth - width) / 2);
        const top = Math.max(12, (viewportHeight - height) / 2);
        setDialogRect({ left, top, width, height });
    }, []);

    const showError = useCallback((error: unknown, nextTitle = t("pages.errorDialog.title"), detailsOverride?: string) => {
        const resolved = resolveErrorMessage(error);
        const resolvedTitle = nextTitle && nextTitle !== "Something went wrong" ? nextTitle : t("pages.errorDialog.title");
        // Try to extract a ticket id from the thrown error (common fields), otherwise generate one
        const extractTicketFromError = (err: unknown): string | null => {
            if (!err || typeof err !== "object") return null;
            const e = err as any;
            const candidates = ["ticket", "ticketId", "ticket_number", "ticketNumber", "id", "reference", "ref"];
            for (const k of candidates) {
                if (k in e && e[k]) return String(e[k]);
            }

            // Some logger helpers embed reference in message or toString()
            try {
                const s = typeof e.toString === "function" ? e.toString() : "";
                const m = s.match(/(T-[0-9a-zA-Z_-]{6,}|[0-9]{5,})/);
                if (m) return m[1];
            } catch (_err) {
                // ignore
            }

            return null;
        };

        const extracted = extractTicketFromError(error);
        const newTicket = extracted ?? (typeof crypto !== "undefined" && (crypto as any).randomUUID
            ? (crypto as any).randomUUID()
            : `T-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`);
        setTitle(resolvedTitle);
        const genericMessage = t("pages.errorDialog.genericMessage", "An unexpected technical error occurred. Please contact customer support and provide the ticket number below for reference.");
        setMessage(genericMessage);
        setDetails(detailsOverride ?? resolved.details ?? null);
        setTicket(newTicket);
        setShowDetails(false);
        updateDialogRect();
        setOpen(true);
    }, [t, updateDialogRect]);

    const copyTicket = useCallback(async () => {
        if (!ticket) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(ticket);
            } else {
                const ta = document.createElement("textarea");
                ta.value = ticket;
                ta.style.position = "fixed";
                ta.style.left = "-9999px";
                document.body.appendChild(ta);
                ta.select();
                document.execCommand("copy");
                document.body.removeChild(ta);
            }
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch (_e) {
            // ignore copy failures
        }
    }, [ticket]);

    useEffect(() => {
        if (!queryClient) {
            return undefined;
        }

        const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
            if (event.type !== "updated" && event.type !== "added") {
                return;
            }

            if (event.query.state.status !== "error") {
                return;
            }

            const error = event.query.state.error;
            const detailKey = JSON.stringify({
                queryKey: event.query.queryKey,
                message: error && typeof error === "object" && "message" in error ? String((error as { message?: unknown }).message) : String(error)
            });

            if (detailKey === lastErrorKey.current) {
                return;
            }

            lastErrorKey.current = detailKey;
            showError(error, "Something went wrong");
        });

        return unsubscribe;
    }, [queryClient, showError]);

    const dismiss = useCallback(() => {
        setOpen(false);
        setDetails(null);
        setShowDetails(false);
    }, []);

    useEffect(() => {
        const onResize = () => {
            if (open) {
                updateDialogRect();
            }
        };

        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, [open, updateDialogRect]);

    useEffect(() => {
        if (typeof document === "undefined") return;
        const prevOverflow = document.body.style.overflow;
        if (open) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = prevOverflow;
        }

        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    const value = useMemo<ErrorDialogContextValue>(() => ({ showError, dismiss }), [showError, dismiss]);

    const footer = (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: isCompact ? "10px 12px" : "12px 16px", flexDirection: isRtl ? "row-reverse" : "row", flexWrap: "wrap" }}>
            {details ? (
                <button
                    type="button"
                    onClick={() => setShowDetails(value => !value)}
                    aria-label={showDetails ? t("pages.errorDialog.hideDetails") : t("pages.errorDialog.moreDetails")}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        flexDirection: isRtl ? "row-reverse" : "row",
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid color-mix(in srgb, var(--color-accent) 40%, var(--color-border))",
                        background: "color-mix(in srgb, var(--color-accent) 12%, var(--color-surface))",
                        color: "var(--color-accent)",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1.2,
                        transition: "background-color 0.2s ease, border-color 0.2s ease"
                    }}
                >
                    <span style={{ display: "inline-flex", transform: showDetails ? (isRtl ? "rotate(-90deg)" : "rotate(90deg)") : "none", transition: "transform 0.15s ease" }}>
                        <Icon name="chevron-right" size="sm" />
                    </span>
                    <span>{showDetails ? t("pages.errorDialog.hideDetails") : t("pages.errorDialog.moreDetails")}</span>
                </button>
            ) : null}
            <div style={{ marginInlineStart: "auto", display: "flex", justifyContent: isRtl ? "flex-start" : "flex-end", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                {ticket ? (
                    <button type="button" onClick={copyTicket} aria-label={t("pages.errorDialog.copyTicket")} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, border: "1px solid color-mix(in srgb, var(--color-border) 20%, transparent)", background: "color-mix(in srgb, var(--color-surface) 90%, transparent)", cursor: "pointer", color: "var(--color-text)", fontWeight: 600, fontSize: 14 }}>
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexDirection: isRtl ? "row-reverse" : "row" }}>
                            <Icon name="copy" size="sm" />
                            <span>{copied ? t("pages.errorDialog.copied") : t("pages.errorDialog.copy")}</span>
                        </div>
                    </button>
                ) : null}
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label={t("pages.errorDialog.close")}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 8,
                        flexDirection: isRtl ? "row-reverse" : "row",
                        padding: "8px 12px",
                        borderRadius: 10,
                        border: "1px solid color-mix(in srgb, var(--color-danger) 40%, var(--color-border))",
                        background: "color-mix(in srgb, var(--color-danger) 12%, var(--color-surface))",
                        color: "var(--color-danger)",
                        fontWeight: 600,
                        cursor: "pointer",
                        fontSize: 14,
                        lineHeight: 1.2,
                        transition: "background-color 0.2s ease, border-color 0.2s ease"
                    }}
                >
                    <Icon name="x" size="sm" />
                    <span>{t("pages.errorDialog.close")}</span>
                </button>
            </div>
        </div>
    );

    const dialogTitle = (
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</span>
    );

    return (
        <ErrorDialogContext.Provider value={value}>
            {children}
            <FlareFloatingWindow
                open={open}
                title={dialogTitle}
                subtitle={t("pages.errorDialog.subtitle")}
                mode="normal"
                onOpenChange={(next: boolean) => {
                    if (!next) dismiss();
                }}
                onModeChange={() => undefined}
                rect={dialogRect}
                onRectChange={setDialogRect}
                modal
                closeOnEscape
                closeOnOutsidePointerDown
                minimizable={false}
                maximizable={false}
                resizable={false}
                autoFocus
                minWidth={280}
                minHeight={180}
                footer={footer}
            >
                <div style={{ display: "grid", gap: 18, minWidth: 0, width: "100%", padding: isCompact ? "12px 12px 0" : "16px 16px 0", direction: isRtl ? "rtl" : "ltr" }}>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                            padding: "12px 14px",
                            borderRadius: 14,
                            background: "color-mix(in srgb, var(--color-surface-elevated) 85%, var(--color-surface) 15%)",
                            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)",
                            minWidth: 0,
                            width: "100%",
                            boxSizing: "border-box"
                        }}
                    >
                        <div style={{ display: "grid", gap: 6, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--color-text)", textAlign: isRtl ? "right" : "left", wordBreak: "break-word" }}>{message}</div>
                            {ticket ? (
                                <div style={{ fontSize: 12, color: "var(--color-muted)", marginTop: 6 }}>
                                    <span>{t("pages.errorDialog.ticketLabel", "Ticket")} #{ticket}</span>
                                </div>
                            ) : null}
                        </div>
                    </div>
                    {details ? (
                        <div style={{ display: "grid", gap: 10 }}>
                            {showDetails ? (
                                <pre style={{ margin: 0, padding: 12, borderRadius: 10, background: "color-mix(in srgb, var(--color-surface-elevated) 80%, #08131d 20%)", border: "1px solid color-mix(in srgb, var(--color-danger) 30%, var(--color-border))", color: "var(--color-text)", overflow: "auto", maxHeight: 180, whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.5, boxSizing: "border-box", textAlign: isRtl ? "right" : "left" }}>
                                    {details}
                                </pre>
                            ) : null}
                        </div>
                    ) : null}
                </div>
            </FlareFloatingWindow>
        </ErrorDialogContext.Provider>
    );
}

export function useGlobalErrorDialog() {
    const context = useContext(ErrorDialogContext);

    if (!context) {
        throw new Error("useGlobalErrorDialog must be used inside GlobalErrorDialogProvider");
    }

    return context;
}
